import { useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";

const normalizeText = (value) =>
  typeof value === "string" ? value.replace(/\s+/g, " ").trim() : value;

function ImageCarousel({ src, alt, caption, width, t, copy }) {
  const images = Array.isArray(src) ? src : [src];
  const [activeIndex, setActiveIndex] = useState(0);
  const hasMultipleImages = images.length > 1;

  const showPrevious = () => {
    setActiveIndex((index) => (index === 0 ? images.length - 1 : index - 1));
  };

  const showNext = () => {
    setActiveIndex((index) => (index === images.length - 1 ? 0 : index + 1));
  };

  return (
    <figure style={{ margin: "24px 0" }}>
      <div
        className="image-carousel"
        style={{
          border: `1px solid ${t.border}`,
          background: t.surface,
        }}
      >
        <div
          className="image-carousel-track"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {images.map((imageSrc, index) => (
            <div className="image-carousel-slide" key={imageSrc}>
              <img
                src={imageSrc}
                alt={hasMultipleImages ? `${alt || copy.projectImage} ${index + 1}` : alt || ""}
                loading={index === 0 ? "eager" : "lazy"}
                style={{
                  width: width || "100%",
                  maxWidth: "100%",
                  height: "auto",
                }}
              />
            </div>
          ))}
        </div>

        {hasMultipleImages && (
          <>
            <button
              type="button"
              aria-label={copy.previousImage}
              className="image-carousel-button image-carousel-button-left"
              onClick={showPrevious}
              style={{
                background: t.bg,
                border: `1px solid ${t.border}`,
                color: t.text,
              }}
            >
              &lt;
            </button>
            <button
              type="button"
              aria-label={copy.nextImage}
              className="image-carousel-button image-carousel-button-right"
              onClick={showNext}
              style={{
                background: t.bg,
                border: `1px solid ${t.border}`,
                color: t.text,
              }}
            >
              &gt;
            </button>
          </>
        )}
      </div>

      <figcaption className="image-carousel-meta" style={{ color: t.dim }}>
        <span>{caption}</span>
        {hasMultipleImages && <span>{activeIndex + 1} / {images.length}</span>}
      </figcaption>

      {hasMultipleImages && (
        <div className="image-carousel-dots" aria-label={copy.chooseImage}>
          {images.map((imageSrc, index) => (
            <button
              type="button"
              key={imageSrc}
              aria-label={`${copy.showImage} ${index + 1}`}
              aria-current={activeIndex === index ? "true" : undefined}
              onClick={() => setActiveIndex(index)}
              style={{
                background: activeIndex === index ? t.link : t.border,
              }}
            />
          ))}
        </div>
      )}
    </figure>
  );
}

function CodeBlock({ lang = "text", content, title, t, copy }) {
  const [copied, setCopied] = useState(false);
  const code = content.trim();

  async function copyCode() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        copyWithFallback(code);
      }
    } catch {
      copyWithFallback(code);
    }

    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  return (
    <div className="code-wrapper">
      <div className="code-toolbar">
        {title ? <div className="code-title">{title}</div> : <span />}
        <button
          type="button"
          onClick={copyCode}
          className="code-copy-button"
          style={{
            color: copied ? t.link : t.dim,
          }}
        >
          {copied ? copy.codeCopied : copy.copyCode}
        </button>
      </div>
      <SyntaxHighlighter
        style={oneDark}
        language={lang}
        customStyle={{
          borderRadius: 8,
          padding: 16,
          fontSize: 14,
          margin: 0,
        }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

function copyWithFallback(value) {
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.left = "-9999px";
  textarea.style.position = "fixed";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

export function BodyRenderer({ body = [], t, copy }) {
  const renderers = {
    p: (content) => (
      <p style={{ color: t.muted, marginBottom: 18, fontSize: 15 }}>
        {normalizeText(content)}
      </p>
    ),

    h3: (content) => (
      <h3 style={{ fontSize: 15, fontWeight: 400, color: t.text, margin: "32px 0 12px" }}>
        {normalizeText(content)}
      </h3>
    ),

    code: (content) => <CodeBlock {...content} t={t} copy={copy} />,

    img: (content) => <ImageCarousel {...content} t={t} copy={copy} />,

    a: ({ href, link }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: t.link, fontSize: 14, textDecoration: "none" }}
      >
        {link}
      </a>
    ),
  };

  return (
    <div>
      {body.map((block, index) => {
        const [type, content] = Object.entries(block)[0];
        const renderer = renderers[type];

        return renderer ? <div key={`${type}-${index}`}>{renderer(content)}</div> : null;
      })}
    </div>
  );
}
